import React, { useState } from "react";
import clsx from "clsx";
import { format, isBefore, parseISO } from "date-fns";
import { motion } from "framer-motion";
import { handleNotificationCall } from "../utils/notificationCallHandler";

function formatTime(iso) {
  try {
    return format(parseISO(iso), "hh:mm a");
  } catch {
    return "";
  }
}

export default function NotificationItem({ notification, isDisabled = false }) {
  const {
    _id,
    leadId,
    leadName,
    isClient,
    scheduledTime,
    note,
    type,
    mobile,
  } = notification;

  const [isLoading, setIsLoading] = useState(false);

  const now = new Date();
  const scheduled = parseISO(scheduledTime);
  const isOverdue = isBefore(scheduled, now);

  const handleCardClick = async (e) => {
    // Prevent interaction if disabled
    if (isDisabled || isLoading) {
      e.preventDefault();
      return;
    }
    
    // Prevent click propagation if clicking on a child element
    if (e.target.closest("button")) return;

    setIsLoading(true);
    try {
      // Use the notification's leadId or extract from ID
      const contactId = leadId || _id;
      await handleNotificationCall(contactId, mobile, isClient, showToast);
    } finally {
      setIsLoading(false);
    }
  };

  const showToast = (message, type) => {
    // Toast will be handled by parent component if needed
    // For now, we can use browser notifications as fallback
    if (type === "error") {
      console.error(message);
    }
  };

  return (
    <motion.div
      className={clsx(
        "rounded-2xl bg-white dark:bg-navy-700 shadow-md px-3 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row items-start gap-3 sm:gap-4 transition-all duration-200 group w-full",
        isDisabled
          ? "opacity-50 cursor-not-allowed pointer-events-none"
          : "hover:shadow-xl hover:-translate-y-1 cursor-pointer",
        isLoading && "opacity-70 pointer-events-none",
        isOverdue
          ? "border border-red-200 dark:border-red-400"
          : "border border-indigo-100 dark:border-indigo-400"
      )}
      onClick={handleCardClick}
      whileHover={!isLoading && !isDisabled ? { scale: 1.015, boxShadow: "0 8px 32px rgba(80,80,180,0.10)" } : {}}
    >
      <div className="flex-shrink-0 mt-1">
        <span
          className={clsx(
            "inline-flex items-center justify-center w-10 h-10 rounded-full",
            isOverdue
              ? "bg-red-50 dark:bg-red-900 text-red-500 dark:text-red-300"
              : "bg-indigo-50 dark:bg-indigo-900 text-indigo-500 dark:text-indigo-300"
          )}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
      <div className="flex-1 min-w-0 w-full">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="font-semibold text-gray-900 dark:text-white text-base sm:text-base">📞 Scheduled Call</span>
          {isOverdue ? (
            <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 dark:bg-red-900 text-red-500 dark:text-red-300 border border-red-200 dark:border-red-400">Overdue</span>
          ) : (
            <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-400">Upcoming</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className={clsx(
            "px-2 py-0.5 rounded-full text-xs font-semibold",
            isClient
              ? "bg-gradient-to-tr from-indigo-500 to-violet-500 text-white"
              : "bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300"
          )}>
            {isClient ? "Client" : "Lead"}
          </span>
          <span className="text-gray-700 dark:text-gray-200 font-medium">{leadName}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-indigo-600 dark:text-indigo-300 font-semibold">{formatTime(scheduledTime)}</span>
          {note && (
            <span className="text-gray-500 dark:text-gray-300 text-sm truncate max-w-[160px] sm:max-w-[220px]">{note}</span>
          )}
          {isLoading && (
            <span className="ml-auto text-xs text-indigo-600 dark:text-indigo-400 font-medium">
              Connecting...
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}
